// Single source of student-facing copy for backend submission states and scoring
// gate flags. Students and organizations must NEVER see operator vocabulary
// (gate / queue / pipeline) or raw status slugs, everything that renders one of
// those to a learner routes through here.

const STATUS_META = {
  scored: { text: 'Marked', cls: 'bg-emerald-50 text-emerald-700', hint: '' },
  queued: { text: 'Marking…', cls: 'bg-amber-50 text-amber-700',
    hint: 'Still marking this response — CLB feedback lands within about 30 seconds. Refresh or reopen to see the score.' },
  pending: { text: 'Saving…', cls: 'bg-slate-100 text-slate-600', hint: '' },
  gate_zeroed: { text: 'Would score 0', cls: 'bg-rose-50 text-rose-700',
    hint: 'This response fell outside the task rules (silence, gibberish, or wrong length), so on the real exam it would score 0.' },
};

export function statusMeta(status) {
  return STATUS_META[status] || {
    text: 'Submitted', cls: 'bg-slate-100 text-slate-600',
    hint: 'Saved. Detailed feedback appears here once it has been marked.',
  };
}

// Multiple empty_recording:* flags map to nearly identical "no audio" copy;
// only the strongest signal is shown so the "Would score 0" block stays scannable.
// Priority: client_silent (definitive) > no_audio_bytes > no_speech > too_short.
const EMPTY_PRIORITY = ['empty_recording:client_silent', 'empty_recording:no_audio_bytes', 'empty_recording:no_speech_detected', 'empty_recording:too_short', 'empty_recording'];

export function dedupeGateFlags(flags) {
  const list = Array.isArray(flags) ? flags.filter(Boolean) : [];
  const emptyMatches = list.filter((f) => String(f).startsWith('empty_recording'));
  if (emptyMatches.length <= 1) return list;
  const chosen = EMPTY_PRIORITY.find((p) => emptyMatches.includes(p)) || emptyMatches[0];
  return list.filter((f) => !String(f).startsWith('empty_recording')).concat(chosen);
}
export function gateFlagText(flag) {
  const f = String(flag || '');
  if (f.startsWith('empty_recording')) {
    if (f.includes('too_short')) return 'The recording was too short to score. Would score 0 on the real exam.';
    if (f.includes('client_silent') || f.includes('no_audio')) return 'No audio was captured. Would score 0 on the real exam.';
    if (f.includes('no_speech')) return 'No speech was detected in the recording. Would score 0 on the real exam.';
    return 'No response was recorded. Would score 0 on the real exam.';
  }
  if (f === 'gibberish' || f === 'ungradable_response') return 'This response could not be graded as English. Would score 0 on the real exam.';
  if (f === 'empty') return 'The response was empty.';
  if (f.startsWith('form_zero')) return 'This response is outside the word range the task requires. Would score 0 on the real exam.';
  if (f.startsWith('underlength')) return 'Under the word count this task asks for.';
  if (f.startsWith('overlength')) return 'Over the word count this task asks for.';
  return 'This response did not meet one of the task rules. Would not score on the real exam.';
}

// Student-facing mock number. Mocks are stored under their internal form id
// (23+) so they never collide with the practice forms (1-22). A learner must
// NEVER see that R&D numbering: form 23 is the FIRST mock and reads "Mock 1"
// (forms 23-52 = 30 mocks). MOCK_FORM_OFFSET = (first mock form id) - 1.
export const MOCK_FORM_OFFSET = 22;
export function mockLabel(form) {
  const n = Number(form) - MOCK_FORM_OFFSET;
  return n > 0 ? n : Number(form); // sub-31 (legacy/practice) shows raw, never negative
}

// Readable task name when no curriculum title is available (fallback only).
export function prettyTask(tt) {
  return String(tt || '')
    .replace(/^r_/, 'reading ')
    .replace(/^l_/, 'listening ')
    .replace(/_/g, ' ')
    .replace(/\bpte\b/i, 'PTE')
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim();
}
